import { useEffect, type RefObject } from "react";

/**
 * Lichtgewicht focus-trap voor overlays/menus.
 *
 * Wanneer `active` true wordt:
 *  - onthouden welk element focus had (of gebruik `returnFocusRef`)
 *  - focus verplaatst naar het eerste focusable element in `containerRef`
 *  - Tab / Shift+Tab blijft in de container (cyclisch)
 *  - Escape roept `onEscape` aan
 *  - bij sluiten keert focus terug naar het onthouden element
 *
 * Wordt gebruikt door de mobiele navigatie en herbruikbaar voor toekomstige
 * modals. Verplaatst geen focus als er geen focusable elementen zijn.
 */
export function useFocusTrap({
  active,
  containerRef,
  onEscape,
  returnFocusRef,
}: {
  active: boolean;
  containerRef: RefObject<HTMLElement | null>;
  onEscape?: () => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      returnFocusRef?.current ?? (document.activeElement as HTMLElement | null);

    const getFocusable = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])",
          ].join(","),
        ),
      ).filter((el) => !el.hasAttribute("aria-hidden") && el.offsetParent !== null);

    // Focus eerste focusable in eerstvolgende frame — na render.
    const focusFrame = requestAnimationFrame(() => {
      const focusables = getFocusable();
      focusables[0]?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onEscape?.();
        return;
      }
      if (event.key !== "Tab") return;

      const focusables = getFocusable();
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      // Keer focus terug — maar niet als focus intussen elders is gezet door
      // een gebruikersactie buiten de container (bv. een link geklikt).
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef, onEscape, returnFocusRef]);
}
